/**
 * Web Audio synth and procedural music: no audio files, everything generated. Effects run
 * through a shared master bus (compressor) with a generated stadium reverb send, so the gun,
 * the PA and the crowd feel like they are in the same big space.
 */
const MUTE_KEY = 'decathlon.muted';
const MUSIC_KEY = 'decathlon.music';
const MUSIC_ON_KEY = 'decathlon.music-on';
const PA_KEY = 'decathlon.pa';

let shared: AudioContext | null = null;
let master: GainNode | null = null;
let reverb: ConvolverNode | null = null;
let reverbGain: GainNode | null = null;

/** Lazily created audio context; must first be touched from a user gesture. */
export function audioContext(): AudioContext | null {
  if (!shared) {
    try {
      shared = new AudioContext();
    } catch {
      return null;
    }
    const comp = shared.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.knee.value = 20;
    comp.ratio.value = 4;
    comp.attack.value = 0.004;
    comp.release.value = 0.2;
    master = shared.createGain();
    master.gain.value = 0.9;
    master.connect(comp).connect(shared.destination);
    // Stadium reverb: 1.8 s of exponentially decaying noise, a touch brighter early on.
    const len = Math.floor(shared.sampleRate * 1.8);
    const ir = shared.createBuffer(2, len, shared.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = ir.getChannelData(ch);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.exp(-i / (shared.sampleRate * 0.45)) * (i < 2000 ? i / 2000 : 1);
    }
    reverb = shared.createConvolver();
    reverb.buffer = ir;
    reverbGain = shared.createGain();
    reverbGain.gain.value = 0.35;
    reverb.connect(reverbGain).connect(master);
  }
  if (shared.state === 'suspended') void shared.resume();
  return shared;
}

/** Connect a node to the master bus, with `wet` (0..1) of it going to the reverb. */
function out(node: AudioNode, wet = 0): void {
  if (!master) return;
  node.connect(master);
  if (wet > 0 && reverb && shared) {
    const send = shared.createGain();
    send.gain.value = wet;
    node.connect(send).connect(reverb);
  }
}

let noiseBuf: AudioBuffer | null = null;
function noise(ctx: AudioContext): AudioBuffer {
  if (!noiseBuf) {
    noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  return noiseBuf;
}

export class Synth {
  muted = false;
  /** Stadium PA announcer via the Web Speech API (optional). */
  pa = true;
  private crowd: { src: AudioBufferSourceNode; base: GainNode; roar: GainNode; roarFilter: BiquadFilterNode; lfo: OscillatorNode } | null = null;
  private crowdLevel = 0;
  private clapTimer: ReturnType<typeof setInterval> | null = null;
  private stepSide = 0;
  private lastGaugeTick = -1;

  constructor() {
    try {
      this.muted = localStorage.getItem(MUTE_KEY) === '1';
      this.pa = localStorage.getItem(PA_KEY) !== '0';
    } catch {
      /* ignore */
    }
  }

  setMuted(m: boolean): void {
    this.muted = m;
    try {
      localStorage.setItem(MUTE_KEY, m ? '1' : '0');
    } catch {
      /* ignore */
    }
    if (master && shared) master.gain.setTargetAtTime(m ? 0 : 0.9, shared.currentTime, 0.05);
    if (m) speechSynthesis?.cancel();
  }

  setPa(on: boolean): void {
    this.pa = on;
    try {
      localStorage.setItem(PA_KEY, on ? '1' : '0');
    } catch {
      /* ignore */
    }
    if (!on) speechSynthesis?.cancel();
  }

  private get audio(): AudioContext | null {
    return this.muted ? null : audioContext();
  }

  // ---------------------------------------------------------------- primitives
  private tone(freq: number, dur: number, type: OscillatorType, vol: number, opts: { slide?: number; at?: number; wet?: number; attack?: number } = {}): void {
    const ctx = this.audio;
    if (!ctx) return;
    const t0 = ctx.currentTime + (opts.at ?? 0);
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t0);
    if (opts.slide) o.frequency.exponentialRampToValueAtTime(Math.max(20, freq + opts.slide), t0 + dur);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(vol, t0 + (opts.attack ?? 0.005));
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    o.connect(g);
    out(g, opts.wet ?? 0.15);
    o.start(t0);
    o.stop(t0 + dur + 0.05);
  }

  /** Filtered noise burst. */
  private burst(dur: number, vol: number, opts: { type?: BiquadFilterType; freq: number; q?: number; slide?: number; at?: number; wet?: number; attack?: number }): void {
    const ctx = this.audio;
    if (!ctx) return;
    const t0 = ctx.currentTime + (opts.at ?? 0);
    const src = ctx.createBufferSource();
    src.buffer = noise(ctx);
    src.loopStart = Math.random() * 1.5;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = opts.type ?? 'bandpass';
    f.frequency.setValueAtTime(opts.freq, t0);
    if (opts.slide) f.frequency.exponentialRampToValueAtTime(Math.max(40, opts.freq + opts.slide), t0 + dur);
    f.Q.value = opts.q ?? 1;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(vol, t0 + (opts.attack ?? 0.003));
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(f).connect(g);
    out(g, opts.wet ?? 0.2);
    src.start(t0, src.loopStart);
    src.stop(t0 + dur + 0.05);
  }

  // ---------------------------------------------------------------- race sounds
  /** Starter's pistol: a sharp crack, a low body, and the stadium echo. */
  gun(): void {
    this.burst(0.09, 1.0, { type: 'highpass', freq: 2500, wet: 0.7 });
    this.burst(0.25, 0.7, { type: 'lowpass', freq: 400, slide: -300, wet: 0.5 });
    this.tone(90, 0.35, 'sine', 0.6, { slide: -60, wet: 0.4 });
    // Slap-back echoes off the far stand.
    this.burst(0.06, 0.25, { type: 'highpass', freq: 2000, at: 0.17, wet: 0.8 });
    this.burst(0.05, 0.12, { type: 'highpass', freq: 1800, at: 0.36, wet: 0.9 });
  }

  /** "On your marks" (low) and "Set" (high) PA cues. */
  cue(final: boolean): void {
    this.tone(final ? 740 : 494, final ? 0.28 : 0.18, 'sine', 0.12, { wet: 0.5, attack: 0.01 });
    this.tone(final ? 740 * 2 : 494 * 2, 0.12, 'triangle', 0.03, { wet: 0.5 });
  }

  /** A slow heartbeat while waiting for the gun. */
  heartbeat(): void {
    this.tone(55, 0.16, 'sine', 0.5, { slide: -20, wet: 0 });
    this.tone(50, 0.14, 'sine', 0.35, { slide: -18, at: 0.16, wet: 0 });
  }

  /** A foot on the track; intensity 0..1 with speed. Alternates left/right timbre. */
  footstep(intensity: number): void {
    this.stepSide ^= 1;
    const f = (this.stepSide ? 240 : 200) + Math.random() * 40;
    this.burst(0.07, 0.18 + 0.3 * intensity, { type: 'lowpass', freq: f * 2.5, slide: -f, wet: 0.08 });
    this.burst(0.03, 0.06 + 0.1 * intensity, { type: 'bandpass', freq: 1800 + Math.random() * 600, q: 1.5, wet: 0.05 });
  }

  /** Foot on the runway board / take-off drive. */
  takeoff(power: number): void {
    this.burst(0.12, 0.5 + power * 0.3, { type: 'lowpass', freq: 500, slide: -350, wet: 0.15 });
    this.burst(0.5, 0.25, { type: 'bandpass', freq: 900, slide: 2400, q: 0.8, attack: 0.05, wet: 0.3 }); // whoosh
  }

  /** Landing in the sand pit. */
  landSand(): void {
    this.burst(0.18, 0.7, { type: 'lowpass', freq: 380, slide: -250, wet: 0.2 });
    this.burst(0.35, 0.3, { type: 'bandpass', freq: 3000, q: 0.5, attack: 0.02, wet: 0.2 }); // sand spray
  }

  /** The put: an effort grunt and the shot leaving the hand. */
  put(power: number): void {
    this.burst(0.18, 0.35 + power * 0.3, { type: 'bandpass', freq: 220, slide: -120, q: 1.2, wet: 0.25 });
    this.burst(0.3, 0.2, { type: 'bandpass', freq: 1200, slide: 1800, q: 0.9, attack: 0.03, wet: 0.3 });
  }

  /** A heavy shot landing in the grass. */
  thud(): void {
    this.burst(0.22, 0.8, { type: 'lowpass', freq: 260, slide: -180, wet: 0.3 });
    this.tone(70, 0.25, 'sine', 0.45, { slide: -40, wet: 0.2 });
  }

  /** Landing on the high jump mat: a soft, deep thump. */
  landMat(): void {
    this.burst(0.3, 0.6, { type: 'lowpass', freq: 220, slide: -150, wet: 0.25 });
  }

  /** The bar comes off and clatters on the uprights and the mat. */
  barRattle(): void {
    for (let i = 0; i < 6; i++) {
      const at = i * (0.09 + Math.random() * 0.05);
      this.tone(1400 + Math.random() * 900, 0.08, 'square', 0.05 * (1 - i / 7), { at, wet: 0.4 });
      this.burst(0.05, 0.12 * (1 - i / 7), { type: 'highpass', freq: 3000, at, wet: 0.4 });
    }
  }

  /** Bar stays up: a bright two-note chime. */
  clear(): void {
    this.tone(880, 0.25, 'triangle', 0.14, { wet: 0.5 });
    this.tone(1320, 0.45, 'triangle', 0.14, { at: 0.12, wet: 0.5 });
  }

  /** Referee's whistle for a foul. */
  whistle(): void {
    const ctx = this.audio;
    if (!ctx) return;
    const t0 = ctx.currentTime;
    const o = ctx.createOscillator();
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    const g = ctx.createGain();
    o.type = 'square';
    o.frequency.value = 2300;
    lfo.frequency.value = 28;
    lfoGain.gain.value = 60;
    lfo.connect(lfoGain).connect(o.frequency);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.09, t0 + 0.02);
    g.gain.setValueAtTime(0.09, t0 + 0.5);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.6);
    o.connect(g);
    out(g, 0.5);
    o.start(t0);
    lfo.start(t0);
    o.stop(t0 + 0.65);
    lfo.stop(t0 + 0.65);
  }

  /** Tick as the angle gauge sweeps; call every frame with the current angle. */
  gaugeTick(angle: number | null): void {
    if (angle === null) {
      this.lastGaugeTick = -1;
      return;
    }
    const step = Math.floor(angle / 5);
    if (step !== this.lastGaugeTick) {
      this.lastGaugeTick = step;
      this.tone(500 + step * 45, 0.04, 'square', 0.035, { wet: 0 });
    }
  }

  /** Same-key press that did not count: a dull tick so the player feels the rule. */
  dud(): void {
    this.tone(160, 0.05, 'triangle', 0.05, { wet: 0 });
  }

  falseStart(): void {
    this.gun();
    setTimeout(() => this.gun(), 320);
  }

  finish(): void {
    [523, 659, 784, 1047].forEach((f, i) => this.tone(f, 0.4, 'triangle', 0.14, { at: i * 0.12, wet: 0.5 }));
    this.tone(1047, 0.9, 'square', 0.04, { at: 0.5, wet: 0.6 });
  }

  /** Personal best sting. */
  fanfare(): void {
    [784, 988, 1175, 1568, 1175, 1568, 1976].forEach((f, i) => this.tone(f, 0.32, 'square', 0.07, { at: i * 0.11, wet: 0.5 }));
    [392, 494, 587].forEach((f) => this.tone(f, 1.1, 'triangle', 0.06, { at: 0.55, wet: 0.6, attack: 0.05 }));
  }

  fail(): void {
    this.tone(330, 0.35, 'sawtooth', 0.08, { slide: -80, wet: 0.3 });
    this.tone(247, 0.6, 'sawtooth', 0.08, { slide: -60, at: 0.3, wet: 0.3 });
  }

  // ---------------------------------------------------------------- crowd
  /** Start the crowd loop (idempotent): a low murmur plus a roar layer that opens up with excitement. */
  crowdStart(): void {
    const ctx = this.audio;
    if (!ctx || this.crowd) return;
    const src = ctx.createBufferSource();
    src.buffer = noise(ctx);
    src.loop = true;
    // Murmur: low-passed, gently wobbling.
    const murmur = ctx.createBiquadFilter();
    murmur.type = 'lowpass';
    murmur.frequency.value = 420;
    const base = ctx.createGain();
    base.gain.value = 0;
    src.connect(murmur).connect(base);
    out(base, 0.6);
    // Roar: band-passed with a slow amplitude wobble so it breathes.
    const roarFilter = ctx.createBiquadFilter();
    roarFilter.type = 'bandpass';
    roarFilter.frequency.value = 900;
    roarFilter.Q.value = 0.6;
    const roar = ctx.createGain();
    roar.gain.value = 0;
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.35;
    lfoGain.gain.value = 0.02;
    lfo.connect(lfoGain).connect(roar.gain);
    src.connect(roarFilter).connect(roar);
    out(roar, 0.7);
    src.start();
    lfo.start();
    this.crowd = { src, base, roar, roarFilter, lfo };
    this.setCrowd(this.crowdLevel);
    // Occasional claps and whistles, denser when excited.
    this.clapTimer = setInterval(() => {
      if (!this.crowd || this.muted) return;
      const e = this.crowdLevel;
      if (Math.random() < 0.15 + e * 0.6) this.burst(0.03, 0.05 + e * 0.12, { type: 'bandpass', freq: 1500 + Math.random() * 1500, q: 2, wet: 0.8 });
      if (e > 0.5 && Math.random() < (e - 0.5) * 0.5) this.tone(1800 + Math.random() * 600, 0.3, 'sine', 0.02, { slide: 400, wet: 0.9, attack: 0.05 });
    }, 140);
  }

  /** excitement 0 (murmur) .. 1 (roar) */
  setCrowd(excitement: number): void {
    this.crowdLevel = excitement;
    const ctx = shared;
    if (!ctx || !this.crowd) return;
    const e = Math.max(0, Math.min(1, excitement));
    this.crowd.base.gain.setTargetAtTime(0.08 + e * 0.08, ctx.currentTime, 0.2);
    this.crowd.roar.gain.setTargetAtTime(e * e * 0.28, ctx.currentTime, 0.25);
    this.crowd.roarFilter.frequency.setTargetAtTime(700 + e * 900, ctx.currentTime, 0.3);
  }

  /** A sudden swell, e.g. crossing the line or clearing the bar. */
  cheer(): void {
    const ctx = shared;
    if (!ctx || !this.crowd) return;
    this.crowd.roar.gain.cancelScheduledValues(ctx.currentTime);
    this.crowd.roar.gain.setValueAtTime(this.crowd.roar.gain.value, ctx.currentTime);
    this.crowd.roar.gain.linearRampToValueAtTime(0.42, ctx.currentTime + 0.25);
    this.crowd.roar.gain.setTargetAtTime(this.crowdLevel * this.crowdLevel * 0.28, ctx.currentTime + 1.2, 0.8);
    for (let i = 0; i < 5; i++) this.tone(1600 + Math.random() * 900, 0.35, 'sine', 0.025, { slide: 500, at: 0.2 + Math.random() * 1.2, wet: 0.9, attack: 0.05 });
  }

  /** A disappointed "ooh". */
  groan(): void {
    const ctx = shared;
    if (!ctx || !this.crowd) return;
    this.crowd.roar.gain.cancelScheduledValues(ctx.currentTime);
    this.crowd.roar.gain.setValueAtTime(this.crowd.roar.gain.value, ctx.currentTime);
    this.crowd.roar.gain.linearRampToValueAtTime(0.2, ctx.currentTime + 0.3);
    this.crowd.roarFilter.frequency.setValueAtTime(600, ctx.currentTime);
    this.crowd.roarFilter.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 1.0);
    this.crowd.roar.gain.setTargetAtTime(this.crowdLevel * this.crowdLevel * 0.28, ctx.currentTime + 1.0, 0.6);
    this.crowd.roarFilter.frequency.setTargetAtTime(700 + this.crowdLevel * 900, ctx.currentTime + 1.2, 0.5);
  }

  crowdStop(): void {
    const ctx = shared;
    if (this.clapTimer) clearInterval(this.clapTimer);
    this.clapTimer = null;
    if (!ctx || !this.crowd) return;
    const c = this.crowd;
    c.base.gain.setTargetAtTime(0, ctx.currentTime, 0.4);
    c.roar.gain.setTargetAtTime(0, ctx.currentTime, 0.4);
    setTimeout(() => {
      c.src.stop();
      c.lfo.stop();
    }, 1500);
    this.crowd = null;
  }

  // ---------------------------------------------------------------- PA announcer
  /** Stadium announcer. Cancels anything still being said. */
  announce(text: string, opts: { rate?: number; pitch?: number } = {}): void {
    if (!this.pa || this.muted) return;
    if (typeof speechSynthesis === 'undefined' || typeof SpeechSynthesisUtterance === 'undefined') return;
    try {
      speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = opts.rate ?? 0.95;
      u.pitch = opts.pitch ?? 0.9;
      u.volume = 0.8;
      const voices = speechSynthesis.getVoices();
      const en = voices.find((v) => v.lang.startsWith('en-GB')) ?? voices.find((v) => v.lang.startsWith('en'));
      if (en) u.voice = en;
      speechSynthesis.speak(u);
    } catch {
      /* no speech: fine */
    }
  }
}

// ------------------------------------------------------------------ music
export type MusicTrack = 'stadium' | 'sprint' | 'anthem';
export const MUSIC_TRACKS: { id: MusicTrack; name: string; blurb: string }[] = [
  { id: 'stadium', name: 'Stadium', blurb: 'Laid-back chiptune' },
  { id: 'sprint', name: 'Sprint', blurb: 'Fast and punchy' },
  { id: 'anthem', name: 'Anthem', blurb: 'Soft pads for the podium' },
];

interface TrackDef {
  bpm: number;
  chords: number[][];
  bass: OscillatorType;
  lead: OscillatorType;
  bassSteps: number[];
  drums: boolean;
  arpRate: 1 | 2;
  volume: number;
}

const TRACKS: Record<MusicTrack, TrackDef> = {
  stadium: {
    bpm: 108,
    chords: [
      [60, 64, 67, 71],
      [57, 60, 64, 67],
      [65, 69, 72, 76],
      [67, 71, 74, 77],
    ],
    bass: 'triangle',
    lead: 'square',
    bassSteps: [0, 3, 6, 8, 11, 14],
    drums: true,
    arpRate: 2,
    volume: 0.09,
  },
  sprint: {
    bpm: 156,
    chords: [
      [57, 60, 64, 67],
      [53, 57, 60, 64],
      [60, 64, 67, 71],
      [55, 59, 62, 65],
    ],
    bass: 'sawtooth',
    lead: 'square',
    bassSteps: [0, 2, 4, 6, 8, 10, 12, 14],
    drums: true,
    arpRate: 1,
    volume: 0.08,
  },
  anthem: {
    bpm: 72,
    chords: [
      [62, 65, 69, 72],
      [58, 62, 65, 69],
      [60, 64, 67, 71],
      [57, 60, 64, 67],
    ],
    bass: 'sine',
    lead: 'triangle',
    bassSteps: [0, 8],
    drums: false,
    arpRate: 2,
    volume: 0.07,
  },
};

const midi = (n: number) => 440 * 2 ** ((n - 69) / 12);

/** Procedural chiptune player with a lookahead scheduler. */
export class Music {
  track: MusicTrack = 'stadium';
  enabled = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private step = 0;
  private nextTime = 0;
  private bus: GainNode | null = null;
  private def: TrackDef | null = null;
  private running = false;
  private wantPlaying = false;

  constructor() {
    try {
      const t = localStorage.getItem(MUSIC_KEY) as MusicTrack | null;
      if (t && MUSIC_TRACKS.some((m) => m.id === t)) this.track = t;
      this.enabled = localStorage.getItem(MUSIC_ON_KEY) === '1';
    } catch {
      /* ignore */
    }
  }

  setTrack(t: MusicTrack): void {
    this.track = t;
    try {
      localStorage.setItem(MUSIC_KEY, t);
    } catch {
      /* ignore */
    }
    this.stop();
    if (this.enabled && this.wantPlaying) this.start();
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    try {
      localStorage.setItem(MUSIC_ON_KEY, on ? '1' : '0');
    } catch {
      /* ignore */
    }
    if (on) {
      if (this.wantPlaying) this.start();
    } else this.stop();
  }

  play(): void {
    this.wantPlaying = true;
    if (this.enabled) this.start();
  }

  pause(): void {
    this.wantPlaying = false;
    this.stop();
  }

  private start(): void {
    if (this.running || !this.enabled) return;
    const ctx = audioContext();
    if (!ctx) return;
    this.def = TRACKS[this.track];
    this.bus = ctx.createGain();
    this.bus.gain.value = 0;
    out(this.bus, 0.1);
    this.bus.gain.setTargetAtTime(this.def.volume, ctx.currentTime, 0.5);
    this.step = 0;
    this.nextTime = ctx.currentTime + 0.05;
    this.running = true;
    this.timer = setInterval(() => this.schedule(ctx), 25);
  }

  private stop(): void {
    if (!this.running) return;
    this.running = false;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    const ctx = shared;
    const m = this.bus;
    if (ctx && m) {
      m.gain.setTargetAtTime(0, ctx.currentTime, 0.15);
      setTimeout(() => m.disconnect(), 600);
    }
    this.bus = null;
  }

  private schedule(ctx: AudioContext): void {
    const def = this.def!;
    const stepDur = 60 / def.bpm / 4;
    while (this.nextTime < ctx.currentTime + 0.12) {
      this.playStep(ctx, this.step, this.nextTime, def, stepDur);
      this.nextTime += stepDur;
      this.step = (this.step + 1) % (16 * def.chords.length);
    }
  }

  private tone(ctx: AudioContext, freq: number, t: number, dur: number, type: OscillatorType, vol: number): void {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.value = freq;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(this.bus!);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  private hit(ctx: AudioContext, t: number, dur: number, vol: number, hp: number): void {
    const src = ctx.createBufferSource();
    src.buffer = noise(ctx);
    const f = ctx.createBiquadFilter();
    f.type = 'highpass';
    f.frequency.value = hp;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f).connect(g).connect(this.bus!);
    src.start(t);
    src.stop(t + dur);
  }

  private playStep(ctx: AudioContext, step: number, t: number, def: TrackDef, stepDur: number): void {
    const bar = Math.floor(step / 16);
    const s = step % 16;
    const chord = def.chords[bar]!;
    if (def.bassSteps.includes(s)) this.tone(ctx, midi(chord[0]! - 24), t, stepDur * 1.8, def.bass, 0.9);
    if (s % def.arpRate === 0) {
      const i = (s / def.arpRate) % (chord.length * 2);
      const idx = i < chord.length ? i : chord.length * 2 - 1 - i;
      const octave = bar % 2 === 1 && s >= 8 ? 12 : 0;
      this.tone(ctx, midi(chord[idx]! + octave), t, stepDur * def.arpRate * 0.9, def.lead, 0.35);
    }
    if (!def.drums && s === 0) for (const n of chord) this.tone(ctx, midi(n), t, stepDur * 16, 'sine', 0.25);
    if (def.drums) {
      if (s % 4 === 0) {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.setValueAtTime(150, t);
        o.frequency.exponentialRampToValueAtTime(40, t + 0.12);
        g.gain.setValueAtTime(1, t);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.15);
        o.connect(g).connect(this.bus!);
        o.start(t);
        o.stop(t + 0.16);
      }
      if (s === 4 || s === 12) this.hit(ctx, t, 0.12, 0.5, 1500);
      if (s % 2 === 0) this.hit(ctx, t, 0.04, 0.18, 7000);
    }
  }
}
